import { useCallback } from "react";
import Container from "modules/Core/sub-modules/ui-kit/components/Container";
import strappedConnected from "modules/Core/higher-order-components/strappedConnected";
import auth from "modules/auth/state";
import PaymentsFeed from "../PaymentsFeed";
import Button from "modules/Core/sub-modules/ui-kit/components/Button";
import Stamp from "modules/Core/sub-modules/ui-kit/components/Stamp";
import cells from "modules/membership/state/index";
import useOnSuccess from "modules/Core/sub-modules/Dialog/hooks/useOnSuccess";
import Card from "modules/Core/sub-modules/ui-kit/components/Card";
import Text from "modules/Core/sub-modules/ui-kit/components/Text";
import Title from "modules/Core/sub-modules/ui-kit/components/Title";
import { SUBSCRIPTION_TIERS } from "modules/membership/consts";
import emptyImage from "modules/membership/assets/empty.png";
import paymentStyles from "../screens/membership-payments-screen/styles.module.scss";
import emptyStyles from "../screens/my-membership-screen/styles.module.scss";

const { cancelMembership } = cells;

const TIER_OVERVIEW = {
    [SUBSCRIPTION_TIERS.BASIC]: {
        title: "Member",
        description: "Booths membership gives you access to unlimited booths and more, to help you continue your growth and development.",
    },
    [SUBSCRIPTION_TIERS.PREMIUM]: {
        title: "Premium Member",
        description: "Booths premium membership gives you access to unlimited booths, monthly coaching sessions and the new project-management booths.",
        coachingMessage: "Please email morrisgaby987@gmail.com to schedule your monthly coaching session.",
    },
};

const SubscriptionTierOverviewCard = ({ tier }) => {
    if (!tier || !TIER_OVERVIEW[tier]) return null;
    const { title, description, coachingMessage } = TIER_OVERVIEW[tier];
    return (
        <Card className={paymentStyles.tierOverviewCard}>
            <Title className={paymentStyles.tierOverviewTitle}>{title}</Title>
            <Text className={paymentStyles.tierOverviewDescription}>{description}</Text>
            {coachingMessage && (
                <div className={paymentStyles.tierOverviewCoaching}>
                    <Text className={paymentStyles.tierOverviewCoachingText}>{coachingMessage}</Text>
                </div>
            )}
        </Card>
    );
};

const CancelSubscriptionButton = strappedConnected(
    Button,
    {},
    { cancelMembership: cancelMembership.action },
    ({ cancelMembership, onSuccess }) => ({
        onConfirm: (close) => cancelMembership({
            callback: () => {
                close();
                onSuccess();
            }
        }),
        text: "Cancel Subscription",
        className: paymentStyles.cancelButton,
        modal: true,
        copy: {
            title: "Are you sure you want to cancel your subscription?",
            description: "This action will cancel your subscription and you will no longer have access to your membership.",
            onCancel: "Back",
            onConfirm: "Cancel Subscription",
        },
    }),
);

const SubscriptionPanel = ({
    hasMembership,
    userId,
    onCancelSuccess,
    isCancelled,
    cancelledDate,
    tier,
}) => {
    if (!hasMembership) {
        return (
            <Container maxWidth center flex col>
                <Card borderless flex col mt3 center contentClassName={emptyStyles.contentContainer}>
                    <img src={emptyImage} alt="No subscription" className={emptyStyles.emptyImage} />
                    <b>You do not have a subscription yet</b>
                    <br />
                    There is plenty on offer here without an active subscription!
                    <br />
                    <br />
                    However, subscriptions offer a way of committing more to the usage of this space to enhance personal growth and development. You can cancel at any time.
                    <br />
                    <br />
                    <Button className={emptyStyles.learnMoreButton} nature="grey" text="Learn More" to="/subscribe" />
                </Card>
            </Container>
        );
    }

    return (
        <Container flex col>
            <Container flex spaceBetween alignCenter mb1>
                <Title>My payments</Title>
                {!isCancelled && (
                    <CancelSubscriptionButton userId={userId} onSuccess={onCancelSuccess} />
                )}
            </Container>
            {isCancelled && (
                <Stamp
                    stamp="Subscription Cancelled"
                    timestamp={cancelledDate}
                    className={paymentStyles.cancelledStamp}
                />
            )}
            <PaymentsFeed userId={userId} />
            <SubscriptionTierOverviewCard tier={tier} />
        </Container>
    );
};

export default strappedConnected(
    SubscriptionPanel,
    {
        user: auth.validateToken.selector,
        membership: auth.validateToken.selectors.membership,
    },
    {
        refetch: auth.validateToken.action,
    },
    ({ user, membership, refetch }) => {
        const success = useOnSuccess();
        const onCancelSuccess = useCallback((result) => {
            if (Boolean(result)) {
                success("Subscription cancelled successfully");
                refetch();
            }
        }, [success, refetch]);
        return {
            hasMembership: Boolean(membership?.id),
            userId: user?.id,
            onCancelSuccess,
            isCancelled: Boolean(user?.membership?.stamps?.completed),
            cancelledDate: user?.membership?.stamps?.completed,
            tier: user?.membership?.tier,
        };
    }
);
